// useComposerInput.js — G3-WS1-T02 (refactor-deepening-r2 / G3, composer React glue).
//
// Owns the SessionComposer's DRAFT model — the typed text plus the two pill lists
// (invoked skills, design elements) — and binds the pure edit operations from
// lib/composer/composeSubmission. The component keeps its own refs/focus/store wiring and
// the per-message attachment queue (useFileAttachments); this hook concentrates the draft
// STATE + the mutation ops so the component reads as render + wiring.
//
// Deletion test (01-DOCTRINE): delete this and the three useState atoms + the
// dedup-append wiring + the mention splice scatter back into the god-component. It is the
// "cola React" layer over the pure engine (engine ← hook ← component).

import { useCallback, useState } from 'react';
import {
  addInvokedSkill,
  addDesignElement,
  removeMentionToken,
  composerHasContent,
  buildSubmissionText,
} from '../lib/composer/composeSubmission.js';

export function useComposerInput() {
  const [text, setText] = useState('');
  // Skills invocadas via `/` viram PILLS (não ficam como /texto cru).
  const [invokedSkills, setInvokedSkills] = useState([]);
  // Elementos do preview (Design Mode) viram PILLS compactas.
  const [designEls, setDesignEls] = useState([]);

  const addSkill = useCallback((cmd) => setInvokedSkills((prev) => addInvokedSkill(prev, cmd)), []);
  const addDesign = useCallback((el) => setDesignEls((prev) => addDesignElement(prev, el)), []);
  const removeMention = useCallback((m) => setText((prev) => removeMentionToken(prev, m)), []);
  // Full-submit reset (text + both pill lists). The /save shortcut clears text + skills
  // only, so it uses the granular setters instead of this.
  const resetDraft = useCallback(() => { setText(''); setInvokedSkills([]); setDesignEls([]); }, []);

  // hasContent takes the (separately-owned) attachments so the guard/flag stay one source.
  const hasContent = useCallback(
    (attachments = []) => composerHasContent({ text, attachments, designEls, invokedSkills }),
    [text, designEls, invokedSkills],
  );
  const buildSubmission = useCallback(
    () => buildSubmissionText({ text, invokedSkills, designEls }),
    [text, invokedSkills, designEls],
  );

  return {
    text, setText,
    invokedSkills, setInvokedSkills, addSkill,
    designEls, setDesignEls, addDesign,
    removeMention, resetDraft,
    hasContent, buildSubmission,
  };
}
